import { useMemo, useState } from "react"
import { Box, Heading, HStack, Icon, Spinner, Text, VStack } from "@chakra-ui/react"
import { FaPlus } from "react-icons/fa6"
import type { AlertRuleState } from "@hydrologiq/shared/components/alerts"
import { MUTED } from "@hydrologiq/shared/components/alerts/alertSummary"
import { fieldsForAssetClass } from "@hydrologiq/shared/components/alerts/customAlertFields"
import { AlignedTitles } from "@hydrologiq/shared/components/TitleWithChips"
import { ScopeChip, ScopeLinkChip } from "@hydrologiq/shared/components/ScopeLinkChip"
import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import { DeleteConfirmDialog } from "@hydrologiq/shared/data-connectors/DeleteConfirmDialog"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { useAssetAlertConfiguration, type CustomAlertDraft } from "../../hooks/useAssetAlertConfiguration"
import { savedConditions } from "./AlertConditionRows"
import { AssetAlertCard, StandardAlertEditor, canEditRule, isCustom } from "./AssetAlertCard"
import { CustomAlertEditor, type CustomAlertFieldGroup } from "./CustomAlertEditor"

export interface AssetAlertConfigurationProps {
  assetId: string
  assetClass?: string | null
}

type AlertFilter = "all" | "standard" | "custom"

const FILTERS: Array<{ value: AlertFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "standard", label: "Standard" },
  { value: "custom", label: "Custom" },
]

/** The card being edited: a saved alert's key, or a new Custom Alert. */
const NEW = "new"
const ruleKey = (rule: AlertRuleState) => `${rule.template.id}:${rule.targetAssetId}`

/**
 * An Asset's alerts, its Modules' included: a parent is a view on its Modules, so their
 * alerts are listed and managed here as on their own pages.
 */
export function AssetAlertConfiguration({ assetId, assetClass }: AssetAlertConfigurationProps) {
  const [filter, setFilter] = useState<AlertFilter>("all")
  const [editing, setEditing] = useState<string | null>(null)
  // The card whose editor last closed, brought back into view
  const [justClosed, setJustClosed] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<AlertRuleState | null>(null)
  const [deletePending, setDeletePending] = useState(false)

  const {
    rules,
    modules,
    loading,
    error,
    enableRule,
    disableRule,
    updateThreshold,
    updateTimeWindow,
    resetToDefaults,
    createCustomAlert,
    updateCustomAlert,
    deleteCustomAlert,
  } = useAssetAlertConfiguration(assetId)

  const listed = [...rules.filter(isCustom), ...rules.filter((rule) => !isCustom(rule))]
  const activeCount = listed.filter((rule) => rule.isEnabled).length
  const customCount = listed.filter(isCustom).length

  // A parent has no Readings of its own: its Custom Alerts judge its Modules
  const isParent = modules.length > 0 || assetClass?.toLowerCase() === "hybrid"
  const fieldGroups: CustomAlertFieldGroup[] = useMemo(
    () =>
      isParent
        ? modules.map((module) => ({ assetId: module.id, assetName: module.name, fields: fieldsForAssetClass(module.class) }))
        : [{ assetId, assetName: "This asset", fields: fieldsForAssetClass(assetClass) }],
    [isParent, modules, assetId, assetClass],
  )
  const hasFields = fieldGroups.some((group) => group.fields.length > 0)
  const moduleNames = useMemo(() => Object.fromEntries(modules.map((m) => [m.id, m.name])), [modules])

  const startEdit = (key: string) => {
    setJustClosed(null)
    setEditing(key)
  }
  const closeEdit = (key: string) => {
    setEditing(null)
    setJustClosed(key === NEW ? null : key)
  }
  const cancelEdit = (key: string, name?: string) => {
    closeEdit(key)
    toaster.info(
      name
        ? { title: "Changes discarded", description: `'${name}' wasn't changed.` }
        : { title: "New alert discarded", description: "Nothing was saved." },
    )
  }

  const startDraft = () => {
    setFilter("custom")
    startEdit(NEW)
  }

  const saveCustomAlert = async (draft: CustomAlertDraft, rule?: AlertRuleState) => {
    const failure = rule ? await updateCustomAlert(rule.template.id, draft) : await createCustomAlert(draft)
    if (!failure) {
      closeEdit(rule ? ruleKey(rule) : NEW)
      toaster.success(
        rule
          ? { title: "Alert saved", description: `'${draft.name}' was saved.` }
          : { title: "Custom alert created", description: `'${draft.name}' is now active.` },
      )
    } else if (!failure.field) {
      // Field errors show on their input; anything else needs telling
      toaster.error({ title: rule ? "Failed to save alert" : "Failed to create alert", description: failure.message })
    }
    return failure
  }

  const confirmDelete = async () => {
    if (!deleting || deletePending) return
    setDeletePending(true)
    try {
      await deleteCustomAlert(deleting.template.id)
      toaster.success({ title: "Alert deleted", description: `'${deleting.template.name}' was deleted.` })
    } catch {
      toaster.error({ title: "Failed to delete alert", description: "Please try again." })
    } finally {
      setDeletePending(false)
      setDeleting(null)
    }
  }

  const rows = listed.filter((rule) => filter === "all" || (filter === "custom") === isCustom(rule))

  const renderRule = (rule: AlertRuleState) => {
    const target = rule.targetAssetId ?? assetId
    const moduleName = target !== assetId ? moduleNames[target] : undefined
    const extraChips = moduleName && (
      <>
        <ScopeLinkChip label="Module" href={`/assets/${target}/settings?tab=alert-configuration`} entityName={moduleName} />
        <ScopeChip>{moduleName}</ScopeChip>
      </>
    )
    const handlers = {
      onToggle: (enabled: boolean) => {
        if (enabled) return enableRule(rule.template.id, target)
        if (rule.assignment) return disableRule(rule.assignment.id)
      },
      onUpdateThreshold: (value: number) => updateThreshold(rule.template.id, value, rule.assignment?.id, target),
      onUpdateTimeWindow: (value: number) => updateTimeWindow(rule.template.id, value, rule.assignment?.id, target),
      onReset: () => {
        if (rule.assignment) return resetToDefaults(rule.assignment.id)
      },
    }
    const key = ruleKey(rule)

    if (editing === key && isCustom(rule)) {
      return (
        <CustomAlertEditor
          key={key}
          fieldGroups={fieldGroups}
          initial={{ name: rule.template.name, conditionGroup: savedConditions(rule) }}
          onSubmit={(draft) => saveCustomAlert(draft, rule)}
          onCancel={() => cancelEdit(key, rule.template.name)}
        />
      )
    }
    if (editing === key) {
      return (
        <StandardAlertEditor
          key={key}
          rule={rule}
          extraChips={extraChips}
          onClose={() => closeEdit(key)}
          onCancel={() => cancelEdit(key, rule.template.name)}
          {...handlers}
        />
      )
    }
    return (
      <AssetAlertCard
        key={key}
        rule={rule}
        extraChips={extraChips}
        assetNames={moduleNames}
        // One card in edit state at a time, so opening another can't drop a draft
        onEdit={canEditRule(rule) && editing === null ? () => startEdit(key) : undefined}
        onDelete={isCustom(rule) ? () => setDeleting(rule) : undefined}
        highlighted={justClosed === key}
        {...handlers}
      />
    )
  }

  let body: React.ReactNode
  if (loading) {
    body = (
      <Box p={6} display="flex" justifyContent="center">
        <Spinner size="lg" />
      </Box>
    )
  } else if (error) {
    body = (
      <Box p={6}>
        <Text color="red.500">Failed to load alert configuration: {error}</Text>
      </Box>
    )
  } else {
    body = (
      // Every card's chips line up after the longest name in the list
      <AlignedTitles>
      <VStack gap={3} align="stretch">
        {editing === NEW && (
          <CustomAlertEditor
            fieldGroups={fieldGroups}
            onSubmit={(draft) => saveCustomAlert(draft)}
            onCancel={() => cancelEdit(NEW)}
          />
        )}
        {rows.map(renderRule)}

        {editing !== NEW && rows.length === 0 && filter === "custom" && (
          <Text fontSize="sm" color={MUTED}>
            No custom alerts yet. Use “Add custom alert” to create one.
          </Text>
        )}
        {rows.length === 0 && filter !== "custom" && (
          <Text>No alert rules are available for this asset. Contact your administrator to configure alerts.</Text>
        )}
      </VStack>
      </AlignedTitles>
    )
  }

  return (
    <VStack gap={5} align="stretch" p={4}>
      <Text fontSize="md" maxW="820px">
        Configure which alerts are active for this asset{modules.length > 0 && " and its modules"}. You can edit
        threshold values for <b>standard</b> alerts, or you can create <b>custom</b> alerts by choosing alert properties
        and conditions. Switch an alert off to edit or delete it.
      </Text>

      <Box>
        <Heading as="h2" fontSize="xl" fontWeight="semibold">
          Alerts
        </Heading>
        {listed.length > 0 && (
          <Text color={MUTED}>
            {activeCount} of {listed.length} active · {customCount} custom
          </Text>
        )}
      </Box>

      {/* The filter and the button that adds to it share a row */}
      <HStack justify="space-between" align="center" gap={4} flexWrap="wrap">
        <SegmentedControl
          items={FILTERS}
          value={filter}
          onValueChange={(details) => setFilter(details.value as AlertFilter)}
          aria-label="Filter alerts"
          variant="hydrologiq_segmented"
        />
        {hasFields && (
          <ActionButton
            onClick={startDraft}
            disabled={editing !== null}
            size="md"
            // No description: a tooltip would replace the button's accessible name
            context={{ label: "Add custom alert", description: "", variant: "primary" }}
          >
            <Icon as={FaPlus} boxSize="12px" />
            Add custom alert
          </ActionButton>
        )}
      </HStack>

      {body}
      <DeleteConfirmDialog
        open={!!deleting}
        connectorName={deleting?.template.name ?? ""}
        noun="alert"
        loading={deletePending}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />
    </VStack>
  )
}

export default AssetAlertConfiguration
